import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import DSDialog from "views/system-design/DSDialog";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSBox from "views/system-design/DSBox";
import DSTextField from "views/system-design/DSTextField";
import DSButton from "views/system-design/DSButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSTypography from "views/system-design/DSTypography";
import DSEnumSelect from "views/system-design/DSEnumSelect";
import DSIconButton from "views/system-design/DSIconButton";
import CloseIcon from "@mui/icons-material/Close";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import TagIcon from "@mui/icons-material/Tag";
import LinkIcon from "@mui/icons-material/Link";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CheckIcon from "@mui/icons-material/Check";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSChip from "views/system-design/DSChip";
import LabelIcon from "@mui/icons-material/Label";
import {
  Attachment,
  Ticket,
  TicketPriority,
  TicketStatus,
  TicketType,
  TICKET_STATUSES,
  TICKET_TYPES,
  TICKET_PRIORITIES,
  TYPE_COLORS,
  REPO_LABELS,
  LABEL_COLORS,
} from "../../types";
import { useTickets } from "../../context/TicketsContext";
import {
  getAssignableUsers,
  sanitizeVersion,
  generateId,
  formatFileSize,
  isImage,
  fileToBase64,
} from "../../utils";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import uploadFile from "utils/uploadFiles";
import useUsers from "components/hooks/useUsers";
import { Author } from "api/author";
import firebaseService from "firebase_service/firebaseService";
import SectionHeader from "../shared/SectionHeader";
import UserPickerField from "../shared/UserPickerField";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import ShopifyPageChips from "../shared/ShopifyPageChips";
import { useIsMobile } from "../../hooks/useIsMobile";

function PropertyField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <DSBox>
      <DSTypography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          mb: 0.5,
          display: "block",
        }}
      >
        {label}
      </DSTypography>
      {children}
    </DSBox>
  );
}

/** Values a caller can prefill, e.g. when a ticket is raised from a Sentry issue. */
export type CreateTicketDraft = {
  title?: string;
  description?: string;
  type?: TicketType;
  priority?: TicketPriority;
  labels?: string[];
  url?: string;
};

interface CreateTicketModalProps {
  onClose: () => void;
  initialValues?: CreateTicketDraft;
  onCreated?: (ticket: Ticket) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export default function CreateTicketModal({
  onClose,
  initialValues,
  onCreated,
}: CreateTicketModalProps) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { createTicket, addAttachment, addSubtask } = useTickets();
  const { data: allUsers } = useUsers();
  const assigneeOptions = getAssignableUsers(allUsers ?? []);
  const [title, setTitle] = useState(initialValues?.title ?? "");
  const [description, setDescription] = useState(
    initialValues?.description ?? "",
  );
  const [status, setStatus] = useState<TicketStatus>("todo");
  const [ticketType, setTicketType] = useState<TicketType>(
    initialValues?.type ?? "feature",
  );
  const [priority, setPriority] = useState(
    initialValues?.priority ?? TICKET_PRIORITIES[0].value,
  );
  const currentUserId = firebaseService.getUserId();
  const currentUser =
    assigneeOptions.find((user) => user.id === currentUserId) ?? null;
  const [selectedReporter, setSelectedReporter] = useState<Author | null>(
    currentUser,
  );
  const [selectedAssignee, setSelectedAssignee] = useState<Author | null>(null);
  const [labels, setLabels] = useState<string[]>(initialValues?.labels ?? []);
  const [targetVersion, setTargetVersion] = useState("");
  const [url, setUrl] = useState(initialValues?.url ?? "");
  const [shopifyPage, setShopifyPage] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [pendingSubtasks, setPendingSubtasks] = useState<
    { id: string; title: string }[]
  >([]);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const newSubtaskInputRef = useRef<HTMLInputElement>(null);
  const [pendingAttachments, setPendingAttachments] = useState<
    { file: File; previewUrl?: string }[]
  >([]);
  const pendingAttachmentsRef = useRef(pendingAttachments);
  pendingAttachmentsRef.current = pendingAttachments;
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdTicket, setCreatedTicket] = useState<{
    id: string;
    key: string;
  } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const canSubmit =
    title.trim().length > 0 && labels.length > 0 && !submitting && !uploading;

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!createdTicket || uploading) return;
    const timer = setTimeout(() => {
      onCloseRef.current();
    }, 5000);
    return () => clearTimeout(timer);
  }, [createdTicket, uploading]);

  const addFiles = useCallback((files: File[]) => {
    const oversized = files.find((file) => file.size > MAX_FILE_SIZE);
    if (oversized) {
      setError(`File "${oversized.name}" exceeds the 10 MB limit.`);
      return;
    }
    setError(null);
    const newEntries = files.map((file) => ({
      file,
      previewUrl: isImage(file.type) ? URL.createObjectURL(file) : undefined,
    }));
    setPendingAttachments((prev) => [...prev, ...newEntries]);
  }, []);

  const removePendingAttachment = (index: number) => {
    setPendingAttachments((prev) => {
      const entry = prev[index];
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter((_, idx) => idx !== index);
    });
  };

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      const files: File[] = [];

      if (clipboardData.files.length > 0) {
        files.push(...Array.from(clipboardData.files));
      } else if (clipboardData.items) {
        for (const item of Array.from(clipboardData.items)) {
          if (item.kind === "file") {
            const file = item.getAsFile();
            if (file) files.push(file);
          }
        }
      }

      if (files.length === 0) return;

      const namedFiles = files.map((file) => {
        if (!file.name || file.name === "image.png") {
          return new File([file], `pasted-image-${Date.now()}.png`, {
            type: file.type,
          });
        }
        return file;
      });

      addFiles(namedFiles);
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [addFiles]);

  useEffect(() => {
    return () => {
      pendingAttachmentsRef.current.forEach((entry) => {
        if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      });
    };
  }, []); // cleanup only on unmount

  const addPendingSubtask = () => {
    const trimmed = newSubtaskTitle.trim();
    if (!trimmed) return;
    setPendingSubtasks((prev) => [
      ...prev,
      { id: generateId(), title: trimmed },
    ]);
    setNewSubtaskTitle("");
    setTimeout(() => newSubtaskInputRef.current?.focus(), 0);
  };

  const removePendingSubtask = (subtaskId: string) => {
    setPendingSubtasks((prev) =>
      prev.filter((subtask) => subtask.id !== subtaskId),
    );
  };

  const [subtaskEditing, setSubtaskEditing] = useState<{
    subtaskId: string;
    draft: string;
  } | null>(null);

  const startEditingSubtask = (subtaskId: string, currentTitle: string) => {
    setSubtaskEditing({ subtaskId, draft: currentTitle });
  };

  const saveSubtaskEdit = () => {
    if (!subtaskEditing) return;
    const trimmed = subtaskEditing.draft.trim();
    if (trimmed) {
      setPendingSubtasks((prev) =>
        prev.map((subtask) =>
          subtask.id === subtaskEditing.subtaskId
            ? { ...subtask, title: trimmed }
            : subtask,
        ),
      );
    }
    setSubtaskEditing(null);
  };

  const cancelSubtaskEdit = () => {
    setSubtaskEditing(null);
  };

  const handleSubtaskDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    setPendingSubtasks((prev) => {
      const reordered = Array.from(prev);
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination!.index, 0, moved);
      return reordered;
    });
  };

  const addCustomLabel = () => {
    const trimmed = newLabel.trim().toLowerCase();
    if (trimmed && !labels.includes(trimmed)) {
      setLabels((prev) => [...prev, trimmed]);
    }
    setNewLabel("");
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createTicket({
        title: title.trim(),
        description,
        status,
        type: ticketType,
        priority,
        assignee: selectedAssignee?.id ?? "",
        labels,
        targetVersion,
        creatorId: selectedReporter?.id ?? "",
        url,
        shopifyPage,
      });

      const errors: string[] = [];

      if (pendingAttachments.length > 0) {
        setUploading(true);
        const failedAttachments: string[] = [];
        await Promise.all(
          pendingAttachments.map(async (entry) => {
            try {
              const base64 = await fileToBase64(entry.file);
              const uploadedUrl = await uploadFile({
                file: base64,
                folder: `tickets/${created.id}`,
              });
              const attachment: Attachment = {
                id: generateId(),
                name: entry.file.name,
                size: entry.file.size,
                type: entry.file.type,
                url: uploadedUrl,
              };
              await addAttachment(created.id, attachment);
            } catch {
              failedAttachments.push(entry.file.name);
            }
          }),
        );
        setUploading(false);

        if (failedAttachments.length > 0) {
          errors.push(
            failedAttachments.length === 1
              ? `Attachment "${failedAttachments[0]}" failed to upload`
              : `Attachments failed: ${failedAttachments.join(", ")}`,
          );
        }
      }

      if (pendingSubtasks.length > 0) {
        const failedSubtasks: string[] = [];
        const subtaskPromises = pendingSubtasks.map(async (subtask) => {
          try {
            await addSubtask(created.id, {
              id: subtask.id,
              title: subtask.title,
              completed: false,
            });
          } catch {
            failedSubtasks.push(subtask.title);
          }
        });
        await Promise.all(subtaskPromises);
        if (failedSubtasks.length > 0) {
          errors.push(
            failedSubtasks.length === 1
              ? `Subtask "${failedSubtasks[0]}" failed to add`
              : `Subtasks failed: ${failedSubtasks.join(", ")}`,
          );
        }
      }

      if (errors.length > 0) {
        setError(`Ticket created, but: ${errors.join(". ")}.`);
      }
      setCreatedTicket({ id: created.id, key: created.key });
      onCreated?.(created);
    } catch {
      setError("Failed to create ticket.");
      setUploading(false);
    } finally {
      setSubmitting(false);
    }
  };

  const createdTicketUrl = useMemo(() => {
    if (!createdTicket) return "";
    // Always the Klydoscope page: the modal also opens from other pages.
    return `${window.location.origin}/admin/tickets?ticket=${createdTicket.id}`;
  }, [createdTicket]);

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(createdTicketUrl)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard API unavailable or denied — silently ignore
      });
  };

  const typeColor = TYPE_COLORS[ticketType] || "grey.600";

  const customLabels = labels.filter((label) => !REPO_LABELS.includes(label));

  return (
    <DSDialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth={isMobile ? undefined : "sm"}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: "visible",
        },
      }}
    >
      <DSBox
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.grey[900]} 0%, ${theme.palette.grey[700]} 100%)`,
          px: isMobile ? 2 : 3,
          py: isMobile ? 2 : 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <DSBox
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: typeColor,
            }}
          />
          <DSTypography
            variant="h6"
            sx={{ color: "common.white", fontWeight: 700, fontSize: 18 }}
          >
            New Ticket
          </DSTypography>
        </DSBox>
        <DSIconButton
          size="small"
          onClick={onClose}
          sx={{ color: "grey.400", "&:hover": { color: "common.white" } }}
        >
          <CloseIcon fontSize="small" />
        </DSIconButton>
      </DSBox>

      <DSDialogContent sx={{ p: 0, overflowY: isMobile ? "auto" : undefined }}>
        <DSBox sx={{ px: isMobile ? 2 : 3, pt: isMobile ? 2 : 3, pb: 2 }}>
          <SectionHeader
            icon={<TitleIcon sx={{ fontSize: 16 }} />}
            title="Details"
          />
          <DSTextField
            placeholder="What needs to be done?"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            fullWidth
            autoFocus
            required
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                fontSize: 16,
                fontWeight: 500,
              },
            }}
          />
          <DSTextField
            placeholder="Add a description..."
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: 14,
                bgcolor: "white.5",
              },
            }}
          />

          {/* Attachments */}
          <DSBox sx={{ mt: 2 }}>
            {pendingAttachments.length > 0 && (
              <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {pendingAttachments.map((entry, index) => (
                  <DSBox
                    key={index}
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      p: 1,
                      bgcolor: "white.5",
                      borderRadius: 1,
                      maxWidth: 160,
                      "&:hover .delete-btn": { opacity: 1 },
                    }}
                  >
                    {entry.previewUrl ? (
                      <img
                        src={entry.previewUrl}
                        alt={`Attachment preview: ${entry.file.name}`}
                        style={{
                          width: 100,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      <InsertDriveFileIcon
                        sx={{ fontSize: 32, color: "text.secondary" }}
                      />
                    )}
                    <DSBox
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <DSBox sx={{ flex: 1, minWidth: 0 }}>
                        <DSTypography
                          variant="caption"
                          noWrap
                          title={entry.file.name}
                        >
                          {entry.file.name}
                        </DSTypography>
                        <DSTypography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            fontSize: 10,
                          }}
                        >
                          {formatFileSize(entry.file.size)}
                        </DSTypography>
                      </DSBox>
                      <DSIconButton
                        className="delete-btn"
                        size="small"
                        onClick={() => removePendingAttachment(index)}
                        sx={{ opacity: 0, transition: "opacity 0.2s" }}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </DSIconButton>
                    </DSBox>
                  </DSBox>
                ))}
              </DSBox>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(event) => {
                const files = event.target.files;
                if (files && files.length > 0) addFiles(Array.from(files));
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              style={{ display: "none" }}
              aria-label="Upload attachments"
            />
            <DSSecondaryButton
              size="small"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Add Attachment
            </DSSecondaryButton>
          </DSBox>

          {/* Subtasks */}
          <DSBox sx={{ mt: 2 }}>
            <SectionHeader
              icon={<ChecklistIcon sx={{ fontSize: 16 }} />}
              title="Subtasks"
            />
            {pendingSubtasks.length > 0 && (
              <DragDropContext onDragEnd={handleSubtaskDragEnd}>
                <Droppable droppableId="pending-subtasks">
                  {(droppableProvided) => (
                    <DSBox
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      {pendingSubtasks.map((subtask, index) => (
                        <Draggable
                          key={subtask.id}
                          draggableId={subtask.id}
                          index={index}
                        >
                          {(draggableProvided, draggableSnapshot) => (
                            <DSBox
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                "&:hover": { bgcolor: "action.hover" },
                                "&:hover .drag-handle": { opacity: 1 },
                                "&:hover .delete-subtask-btn": { opacity: 1 },
                                ...(draggableSnapshot.isDragging && {
                                  bgcolor: "action.hover",
                                  boxShadow: 2,
                                }),
                              }}
                            >
                              <DSBox
                                {...draggableProvided.dragHandleProps}
                                className="drag-handle"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  opacity: 0,
                                  transition: "opacity 0.2s",
                                  cursor: "grab",
                                  color: "text.secondary",
                                }}
                              >
                                <DragIndicatorIcon sx={{ fontSize: 16 }} />
                              </DSBox>
                              {subtaskEditing?.subtaskId === subtask.id ? (
                                <DSTextField
                                  value={subtaskEditing.draft}
                                  onChange={(event) =>
                                    setSubtaskEditing((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            draft: event.target.value,
                                          }
                                        : prev,
                                    )
                                  }
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === "Enter" &&
                                      (event.metaKey || event.altKey)
                                    ) {
                                      event.preventDefault();
                                      saveSubtaskEdit();
                                    } else if (event.key === "Escape") {
                                      cancelSubtaskEdit();
                                    }
                                  }}
                                  onBlur={saveSubtaskEdit}
                                  size="small"
                                  fullWidth
                                  multiline
                                  maxRows={4}
                                  autoFocus
                                  sx={{ flex: 1 }}
                                />
                              ) : (
                                <DSTypography
                                  variant="body2"
                                  onClick={() =>
                                    startEditingSubtask(
                                      subtask.id,
                                      subtask.title,
                                    )
                                  }
                                  sx={{
                                    flex: 1,
                                    cursor: "text",
                                    borderRadius: 0.5,
                                    px: 0.5,
                                    whiteSpace: "pre-wrap",
                                    "&:hover": { bgcolor: "action.hover" },
                                  }}
                                >
                                  {subtask.title}
                                </DSTypography>
                              )}
                              <DSIconButton
                                className="delete-subtask-btn"
                                size="small"
                                onClick={() => removePendingSubtask(subtask.id)}
                                sx={{
                                  opacity: 0,
                                  transition: "opacity 0.2s",
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </DSIconButton>
                            </DSBox>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </DSBox>
                  )}
                </Droppable>
              </DragDropContext>
            )}
            <DSBox sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
              <DSTextField
                inputRef={newSubtaskInputRef}
                value={newSubtaskTitle}
                onChange={(event) => setNewSubtaskTitle(event.target.value)}
                placeholder={`Add a subtask... (${typeof navigator !== "undefined" && navigator.platform.includes("Mac") ? "⌘" : "Alt"}+Enter to add)`}
                size="small"
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    (event.metaKey || event.altKey)
                  ) {
                    event.preventDefault();
                    addPendingSubtask();
                  }
                }}
              />
              <DSIconButton
                size="small"
                onClick={addPendingSubtask}
                sx={{ mt: 0.5 }}
              >
                <AddIcon fontSize="small" />
              </DSIconButton>
            </DSBox>
          </DSBox>
        </DSBox>

        <DSBox
          sx={{
            px: isMobile ? 2 : 3,
            py: 2.5,
            bgcolor: "white.5",
            borderTop: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <SectionHeader
            icon={<DescriptionIcon sx={{ fontSize: 16 }} />}
            title="Properties"
          />
          <DSBox
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
              gap: 2,
            }}
          >
            <PropertyField label="Status">
              <DSEnumSelect<TicketStatus>
                value={status}
                options={TICKET_STATUSES}
                onChange={setStatus}
                size="small"
                fullWidth
              />
            </PropertyField>
            <PropertyField label="Type">
              <DSEnumSelect<TicketType>
                value={ticketType}
                options={TICKET_TYPES}
                onChange={setTicketType}
                size="small"
                fullWidth
              />
            </PropertyField>
            <PropertyField label="Priority">
              <DSEnumSelect
                value={priority}
                options={TICKET_PRIORITIES}
                onChange={setPriority}
                size="small"
                fullWidth
              />
            </PropertyField>
          </DSBox>
        </DSBox>

        <DSBox
          sx={{
            px: isMobile ? 2 : 3,
            py: 2.5,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 3,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <UserPickerField
            label="Reporter"
            placeholder="No reporter"
            selectedUser={selectedReporter}
            options={assigneeOptions}
            onChange={setSelectedReporter}
          />
          <DSBox
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "flex-end",
              gap: "10px",
            }}
          >
            <UserPickerField
              label="Assignee"
              placeholder="Unassigned"
              selectedUser={selectedAssignee}
              options={assigneeOptions}
              onChange={setSelectedAssignee}
            />
            {selectedReporter &&
              selectedAssignee?.id !== selectedReporter.id && (
                <DSSecondaryButton
                  size="small"
                  onClick={() => setSelectedAssignee(selectedReporter)}
                  sx={{ borderRadius: 2, fontSize: 11, mb: 0.5 }}
                >
                  Assign to me
                </DSSecondaryButton>
              )}
          </DSBox>
        </DSBox>

        <DSBox sx={{ px: isMobile ? 2 : 3, pt: 2.5, pb: 2.5 }}>
          <SectionHeader
            icon={<TagIcon sx={{ fontSize: 16 }} />}
            title="Version"
          />
          <DSTextField
            value={targetVersion}
            onChange={(event) =>
              setTargetVersion(sanitizeVersion(event.target.value))
            }
            fullWidth
            size="small"
            placeholder="e.g. 7.2.0"
            sx={{ mb: 2 }}
          />

          <SectionHeader
            icon={<LinkIcon sx={{ fontSize: 16 }} />}
            title="Link"
          />
          <DSTextField
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            fullWidth
            size="small"
            placeholder="https://..."
            sx={{ mb: 2 }}
          />

          <SectionHeader
            icon={<LabelIcon sx={{ fontSize: 16 }} />}
            title="Labels *"
          />
          <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            {REPO_LABELS.map((label) => {
              const isSelected = labels.includes(label);
              return (
                <DSChip
                  key={label}
                  label={label}
                  size="small"
                  variant={isSelected ? "filled" : "outlined"}
                  onClick={() =>
                    setLabels((prev) =>
                      isSelected
                        ? prev.filter((existing) => existing !== label)
                        : [...prev, label],
                    )
                  }
                  sx={{
                    cursor: "pointer",
                    ...(isSelected && {
                      bgcolor: LABEL_COLORS[label] ?? "grey.500",
                      color: "common.white",
                      fontWeight: 600,
                    }),
                  }}
                />
              );
            })}
          </DSBox>

          {/* Custom labels */}
          {customLabels.length > 0 && (
            <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
              {customLabels.map((label) => (
                <DSChip
                  key={label}
                  label={label}
                  size="small"
                  onDelete={() =>
                    setLabels((prev) =>
                      prev.filter((existing) => existing !== label),
                    )
                  }
                />
              ))}
            </DSBox>
          )}
          <DSBox sx={{ display: "flex", gap: 0.5 }}>
            <DSTextField
              value={newLabel}
              onChange={(event) => setNewLabel(event.target.value)}
              size="small"
              placeholder="Custom label"
              fullWidth
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addCustomLabel();
                }
              }}
            />
            <DSIconButton size="small" onClick={addCustomLabel}>
              <AddIcon fontSize="small" />
            </DSIconButton>
          </DSBox>

          {/* Shopify Page (conditional) */}
          {labels.includes("shopify") && (
            <DSBox sx={{ mt: 2 }}>
              <SectionHeader
                icon={<StorefrontIcon sx={{ fontSize: 16 }} />}
                title="Shopify Page"
              />
              <ShopifyPageChips
                selectedPage={shopifyPage}
                onPageChange={setShopifyPage}
              />
            </DSBox>
          )}
        </DSBox>
      </DSDialogContent>

      <DSBox
        sx={{
          display: "flex",
          flexWrap: isMobile ? "wrap" : "nowrap",
          alignItems: "center",
          gap: isMobile ? 1 : 1.5,
          px: isMobile ? 2 : 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "white.5",
        }}
      >
        {error && (
          <DSTypography
            variant="caption"
            sx={{ color: "error.main", width: isMobile ? "100%" : "auto" }}
          >
            {error}
          </DSTypography>
        )}
        {createdTicket ? (
          <>
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckIcon sx={{ fontSize: 18, color: "success.main" }} />
              <DSTypography
                variant="body2"
                sx={{ fontWeight: 600, color: "success.main" }}
              >
                {createdTicket.key} created
              </DSTypography>
            </DSBox>
            <DSSecondaryButton
              startIcon={
                linkCopied ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <LinkIcon fontSize="small" />
                )
              }
              onClick={handleCopyLink}
              sx={{ borderRadius: 2, px: 2 }}
            >
              {linkCopied ? "Copied!" : "Copy Link"}
            </DSSecondaryButton>
            <DSBox sx={{ flex: 1 }} />
            <DSButton onClick={onClose} sx={{ borderRadius: 2, px: 3 }}>
              Done
            </DSButton>
          </>
        ) : (
          <>
            <DSBox sx={{ flex: 1 }} />
            <DSSecondaryButton
              onClick={onClose}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Cancel
            </DSSecondaryButton>
            <DSPrimaryButton
              onClick={handleCreate}
              disabled={!canSubmit}
              sx={{
                borderRadius: 2,
                px: isMobile ? 3 : 4,
                fontWeight: 700,
                textTransform: "none",
                boxShadow: canSubmit ? 2 : 0,
              }}
            >
              {submitting || uploading ? (
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DSCircularProgress size={16} color="inherit" />
                  {uploading ? "Uploading..." : "Creating..."}
                </DSBox>
              ) : (
                "Create Ticket"
              )}
            </DSPrimaryButton>
          </>
        )}
      </DSBox>
    </DSDialog>
  );
}
